import { action, observable } from 'mobx';

import { mergeQueryString, QueryCombiner, SerializedData, updateSearch } from 'bernie-core';
import { Store } from 'bernie-plugin-mobx';
import { DynamicElementsStore, PriceSummaryStore, FlightInformationStore } from '.';
import { FlightsSelectedJourneyReviewType } from '/types/shared/flights-selected-journey-review';
import {
  FlightsStandardOffer,
  FlightsBargainFareOffer,
  FlightsShoppingPwaFlightInformationResults,
  FlightsSelectedJourneyAvailableFares
} from '../__generated__/api';
import FlightsJourneyDetailsDynamicElements = FlightsShoppingPwaFlightInformationResults.FlightsJourneyDetailsDynamicElements;
import FareTypes = FlightsSelectedJourneyAvailableFares.Fares;
import DynamicElements = FlightsShoppingPwaFlightInformationResults.FlightsInformationDynamicElementsInlineFragment;
import OnSelectAnalytics = FlightsShoppingPwaFlightInformationResults.OnSelectAnalytics;

export class FlightsSelectedJourneyReviewStore extends Store {
  @observable public flightsSelectedJourneyReview: FlightsSelectedJourneyReviewType[];

  public selectedIdentifiers: Map<number, string>;
  public defaultFareIndex = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public dialogActionsMap: Map<number, any> = new Map();
  public onFareSelectAnalytics: OnSelectAnalytics[] = [];

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  @action
  public setFareDetails = flightsSelectedJourneyReview => {
    this.flightsSelectedJourneyReview.map((flightsSelectedJourneyReviewItem, index) =>
      this.setFareSeatMapInformation(flightsSelectedJourneyReview, flightsSelectedJourneyReviewItem.fareDetails, index)
    );
  };

  @action
  public setFareSeatMapInformation = (flightsSelectedJourneyReview, fareDetail, index: number) => {
    const { fareDetails } = flightsSelectedJourneyReview[index] as FlightsSelectedJourneyReviewType;
    const { fareSeatMapInformation } = fareDetails;

    fareDetail.fareSeatMapInformation = fareSeatMapInformation;
  };

  public flightsSelectedJourneyReviewItem = (index: number): FlightsSelectedJourneyReviewType => {
    const flightsSelectedJourneyReview = this.flightsSelectedJourneyReview && this.flightsSelectedJourneyReview[index];

    return flightsSelectedJourneyReview;
  };

  public set bargainJourneyReviewItem(
    availableFaresInformationList: FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation[]
  ) {
    const flightsSelectedJourneyReview = availableFaresInformationList.map(availableFaresInformation =>
      this.populateJourneyItem(availableFaresInformation)
    );
    this.hydrate({ flightsSelectedJourneyReview });
  }

  public set journeyReviewItem(
    availableFaresInformation: FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
  ) {
    const flightsSelectedJourneyReview = [this.populateJourneyItem(availableFaresInformation)];
    this.hydrate({ flightsSelectedJourneyReview });
  }

  private populateJourneyItem = (
    availableFaresInformation:
      | FlightsStandardOffer.FlightsJourneyAvailableFaresInformation
      | FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation
  ) => {
    return {
      flightsJourneyHeaders: availableFaresInformation.flightsJourneyHeaders,
      flightsJourneySummary: availableFaresInformation.flightsJourneySummary,
      flightsJourneyInformation: {
        details: availableFaresInformation.flightsJourneyInformation?.details,
        flightJourneyDetails: availableFaresInformation.flightsJourneyInformation?.flightJourneyDetails
      }
    };
  };

  @action
  public setFlightsSelectedJourneyReview = (journeyDetailsList: FlightsJourneyDetailsDynamicElements[]) => {
    if (journeyDetailsList) {
      this.flightsSelectedJourneyReview.map((selectedJourneyItem, selectedJourneyIndex) => {
        if (this.verifyFareSummaryPresent(selectedJourneyItem)) {
          this.setFlightsSeatMapInfo(selectedJourneyItem, journeyDetailsList[selectedJourneyIndex]);
          selectedJourneyItem.fareDetails.fareSummary.title = journeyDetailsList[selectedJourneyIndex].fareName;
          selectedJourneyItem.fareDetails.fareSummary.flightsFareNudgeMessage =
            journeyDetailsList[selectedJourneyIndex].flightsFareNudgeMessage;
          if (journeyDetailsList[selectedJourneyIndex].fareChangeAction) {
            selectedJourneyItem.fareDetails.fareSummary.details =
              journeyDetailsList[selectedJourneyIndex].fareChangeAction;
          }
          selectedJourneyItem.flightsJourneyInformation.flightJourneyDetails.journeySections.map(
            (item, journeySectionIndex) =>
              item.journeyConnectionInformation.map(connectionInformation => {
                if (
                  connectionInformation.__typename === 'FlightsConnectionInformation' &&
                  connectionInformation.flightsConnection.cabinClassAndBookingCode &&
                  journeyDetailsList[selectedJourneyIndex].journeyPartsDynamicElements[journeySectionIndex]
                ) {
                  connectionInformation.flightsConnection.cabinClassAndBookingCode =
                    journeyDetailsList[selectedJourneyIndex].journeyPartsDynamicElements[
                      journeySectionIndex
                    ].cabinClassAndBookingCode;
                }
              })
          );
        }
      });
    }
  };

  private setFlightsSeatMapInfo = (
    selectedJourneyItem: FlightsSelectedJourneyReviewType,
    journeyDetails: FlightsJourneyDetailsDynamicElements
  ): void => {
    const fareSeatMapInformation = selectedJourneyItem.fareDetails.fareSeatMapInformation;
    if (fareSeatMapInformation) {
      fareSeatMapInformation.fareSeatMapViewDetails.flightLegsDetails =
        journeyDetails.fareSeatMapDynamicElement?.flightLegsDetails;
      fareSeatMapInformation.selectedSeatDetails = journeyDetails.selectedSeatDetails;
      fareSeatMapInformation.title = journeyDetails.title;
      fareSeatMapInformation.message = journeyDetails.message;
      fareSeatMapInformation.details = journeyDetails.details;
    }
  };

  @action
  public initialiseDynamicElementsIdentifier() {
    if (this.flightsSelectedJourneyReview && this.flightsSelectedJourneyReview.length) {
      this.selectedIdentifiers = new Map();
      this.flightsSelectedJourneyReview.forEach((flightsSelectedJourneyReview, index) => {
        const identifier = this.getSelectedJourneyIdentifier(flightsSelectedJourneyReview);
        if (identifier) {
          this.selectedIdentifiers.set(index, identifier);
        }
      });
    }
  }

  @action
  private setSelectedFares = (identifier: string) => {
    if (identifier) {
      this.flightsSelectedJourneyReview.forEach(selectedJourneyItem => {
        if (this.verifyFareSummaryPresent(selectedJourneyItem)) {
          const { flightsSelectedJourneyAvailableFaresInformation } = selectedJourneyItem.fareDetails.fareSummary;
          const fares = flightsSelectedJourneyAvailableFaresInformation.fareChoiceInformation.fares;
          const fare = fares.find(fare => fare.identifier === identifier);
          if (fare) {
            this.setActionlabels(fares, identifier);
            fares.forEach(fare => (fare.selected = false));
            fare.selected = true;
          }
        }
      });
    }
  };

  private setActionlabels = (fares: Array<FlightsSelectedJourneyAvailableFares.Fares>, identifier: string) => {
    fares.filter(fare => fare.selected || fare.identifier === identifier).forEach(this.swapLabelWithAltText);
  };

  private swapLabelWithAltText = (fare: FlightsSelectedJourneyAvailableFares.Fares) => {
    [fare.chooseFareAction.displayAction, fare.selectFareActionAltText] = [
      fare.selectFareActionAltText,
      fare.chooseFareAction.displayAction
    ];
  };

  private verifyFareSummaryPresent(flightsSelectedJourneyReview: FlightsSelectedJourneyReviewType): boolean {
    const { fareDetails } = flightsSelectedJourneyReview;
    const { fareSummary } = fareDetails || {};
    if (
      flightsSelectedJourneyReview &&
      fareDetails &&
      fareSummary &&
      fareSummary.flightsSelectedJourneyAvailableFaresInformation
    ) {
      return true;
    }

    return false;
  }

  private getSelectedJourneyIdentifier(flightsSelectedJourneyReview: FlightsSelectedJourneyReviewType): string {
    if (this.verifyFareSummaryPresent(flightsSelectedJourneyReview)) {
      return flightsSelectedJourneyReview.fareDetails.fareSummary.flightsSelectedJourneyAvailableFaresInformation
        .fareChoiceInformation.fares[this.defaultFareIndex].identifier;
    }
  }

  public updateDynamicIdentifier = (index: number, identifier: string) => {
    if (this.selectedIdentifiers && this.selectedIdentifiers.get(index)) {
      this.selectedIdentifiers.set(index, identifier);
    }
  };

  public getIdentifier = () => {
    let appendedIdentifier;
    if (this.selectedIdentifiers && this.selectedIdentifiers.size) {
      this.selectedIdentifiers.forEach((identifier, index) => {
        appendedIdentifier = appendedIdentifier
          ? appendedIdentifier.concat(this.selectedIdentifiers.get(index))
          : this.selectedIdentifiers.get(index);
      });
    }

    return appendedIdentifier;
  };

  public updateUrl = (
    identifier: string,
    history,
    location,
    dynamicElementsStore: DynamicElementsStore,
    pwaDialog?,
    upsellOfferToken?: string
  ) => {
    const ancillaryIdNew = dynamicElementsStore.getSelectedAncillaryId();
    const queryCombiner: QueryCombiner = (prevQuery, newQuery) => newQuery;
    updateSearch({
      history,
      location,
      newParams: mergeQueryString(location.search, {
        identifier: identifier,
        pwaDialog: pwaDialog,
        ancillaryId: ancillaryIdNew ?? undefined,
        upsellOfferToken: upsellOfferToken ?? undefined
      }),
      replace: true,
      queryCombiner
    });
  };

  public updateInformation = (
    itemIndex: number,
    identifier: string,
    dynamicElementsStore: DynamicElementsStore,
    priceSummaryStore: PriceSummaryStore,
    history,
    location,
    pwaDialog?: string,
    upsellOfferToken?: string
  ) => {
    const identifierToUpdateElements = this.updatePriceSummary(
      itemIndex,
      identifier,
      dynamicElementsStore,
      priceSummaryStore
    );
    this.updateFarePrice(itemIndex, identifier, dynamicElementsStore);

    dynamicElementsStore.setSelectedIdentifier(identifierToUpdateElements);
    this.updateFlightDetails(identifierToUpdateElements, dynamicElementsStore);
    this.updateUrl(identifierToUpdateElements, history, location, dynamicElementsStore, pwaDialog, upsellOfferToken);
  };

  private updateFlightDetails = (identifierToUpdateElements: string, dynamicElementsStore: DynamicElementsStore) => {
    const journeyDetailsList = dynamicElementsStore.getFlightsJourneyDetailsById(identifierToUpdateElements);
    this.setOnSelectAnalytics(journeyDetailsList);
    this.setFlightsSelectedJourneyReview(journeyDetailsList);
    dynamicElementsStore.setSelectedIdentifier(identifierToUpdateElements);
  };

  private setOnSelectAnalytics = (journeyDetailsList: FlightsJourneyDetailsDynamicElements[]): void => {
    if (journeyDetailsList) {
      journeyDetailsList
        .filter(flightsJourneyDetails => flightsJourneyDetails.fareSeatMapDynamicElement)
        .forEach(flightsJourneyDetails =>
          this.onFareSelectAnalytics.push(...flightsJourneyDetails.fareSeatMapDynamicElement?.onSelectAnalytics)
        );
    }
  };

  private updatePriceSummary(
    itemIndex: number,
    identifier: string,
    dynamicElementsStore: DynamicElementsStore,
    priceSummaryStore: PriceSummaryStore
  ): string {
    this.updateDynamicIdentifier(itemIndex, identifier);
    const appendedLegsIdentifier = this.getIdentifier();
    let identifierToUpdateElements;
    let priceSummary = dynamicElementsStore.getPriceSummaryById(appendedLegsIdentifier);
    if (priceSummary === null || priceSummary === undefined) {
      const identifierForSameFares = identifier.repeat(this.flightsSelectedJourneyReview?.length);
      priceSummary = dynamicElementsStore.getPriceSummaryById(identifierForSameFares);
      identifierToUpdateElements = identifierForSameFares;
    } else {
      identifierToUpdateElements = appendedLegsIdentifier;
    }
    priceSummaryStore.setPriceSummary(priceSummary);
    this.setSelectedFares(identifier);

    return identifierToUpdateElements;
  }

  private getFareCombinations(
    identifier: string,
    dynamicElementsStore: DynamicElementsStore
  ): Map<string, DynamicElements> {
    const combinationMap = new Map();
    if (dynamicElementsStore.dynamicElementsMap) {
      for (const [dynamicElementIdentifier, dynamicElements] of dynamicElementsStore.dynamicElementsMap.entries()) {
        if (dynamicElementIdentifier.includes(identifier)) {
          combinationMap.set(dynamicElementIdentifier, dynamicElements);
        }
      }
    }

    return combinationMap;
  }

  private updateFareFromCombinationMap = (fareCombinationMap: Map<string, DynamicElements>, fare: FareTypes) => {
    for (const [dynamicElementIdentifier, dynamicElements] of fareCombinationMap.entries()) {
      if (dynamicElementIdentifier.includes(fare.identifier)) {
        fare.totalPrice = dynamicElements.totalPrice;
        fare.priceLockup.lockupPrice =
          dynamicElements.priceSummary.tripTotalDetails.tripTotal.roundedPrice ??
          dynamicElements.priceSummary.tripTotalDetails.tripTotal.price;
        fare.priceLockup.priceSubtextFirst = [dynamicElements.totalPrice];
        fare.upsellOfferToken = dynamicElements.airOfferToken;

        const farePricePerPerson = dynamicElements.fareChoiceInformationDynamicElement?.farePricePerPerson;
        if (farePricePerPerson) {
          fare.formattedPrice.completeText = farePricePerPerson;
          fare.formattedPrice.items[0].text = farePricePerPerson;
        }
      }
    }
  };

  private updateFarePrice = (itemIndex: number, identifier: string, dynamicElementsStore: DynamicElementsStore) => {
    const fareCombinationMap = this.getFareCombinations(identifier, dynamicElementsStore);
    this.flightsSelectedJourneyReview
      .filter(
        (flightsSelectedJourneyReviewItem, index) =>
          index !== itemIndex && this.verifyFareSummaryPresent(flightsSelectedJourneyReviewItem)
      )
      .forEach(flightsSelectedJourneyReviewItem => {
        flightsSelectedJourneyReviewItem.fareDetails.fareSummary.flightsSelectedJourneyAvailableFaresInformation.fareChoiceInformation.fares.forEach(
          fare => {
            this.updateFareFromCombinationMap(fareCombinationMap, fare);
          }
        );
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public setChangeFlightDialogAction = (legIndex: number, dialogActions: any) => {
    this.dialogActionsMap[legIndex] = dialogActions;
  };

  private updateAncillaryId = (ancillaryIdUpdated: string, identifierNew: string, location, history): void => {
    const ancillaryIdQuery = {
      ancillaryId: ancillaryIdUpdated,
      identifier: identifierNew ?? undefined,
      pwaDialog: undefined,
      pwaDialogNested: undefined
    };
    const queryCombiner: QueryCombiner = (prevQuery, newQuery) => newQuery;
    updateSearch({
      history,
      location,
      newParams: mergeQueryString(location.search, ancillaryIdQuery),
      replace: true,
      queryCombiner
    });
  };

  public updateAncillaryIdFareDetails = (
    dynamicElementsStore: DynamicElementsStore,
    flightInformationStore: FlightInformationStore,
    location,
    history
  ) => {
    const ancillaryId = dynamicElementsStore.getSelectedAncillaryId(flightInformationStore.baseOfferAncillaryID);
    if (ancillaryId) {
      this.updateAncillaryId(ancillaryId, dynamicElementsStore.selectedIdentifier, location, history);
    } else if (location.search.includes('ancillaryId')) {
      this.updateAncillaryId(undefined, dynamicElementsStore.selectedIdentifier, location, history);
    }
  };

  public cleanSelectedAnalytics = () => {
    this.onFareSelectAnalytics = [];
  };
}

FlightsSelectedJourneyReviewStore.DEFAULTS = {
  defaultFareIndex: 0
};
